import type { IntegrationPersistCategory } from 'types/integrations/integration-connection';

export type IntegrationUiCategory = 'ai' | 'cloud' | 'productivity';

export type IntegrationDefinition = {
  id: string;
  /** קטגוריה לשמירה ב-JSON */
  persistCategory: IntegrationPersistCategory;
  /** טאב פנימי במסך */
  uiCategory: IntegrationUiCategory;
  /** שם ספק ל-API / JSON (אנגלית) */
  provider: string;
  /** כותרת בעברית לכרטיס */
  titleHe: string;
  /** תיאור בעברית */
  description: string;
  /** תיאור באנגלית (כשהממשק באנגלית) */
  descriptionEn: string;
  /** apiKey — דיאלוג מפתח + מודל; oauth — כפתור OAuth (סימולציה) */
  mode: 'apiKey' | 'oauth';
  oauthLabel?: 'google' | 'microsoft' | 'slack' | 'dropbox';
  /** רשימת מודלים לבחירה (רק AI) */
  modelOptions?: string[];
};

export const INTEGRATION_DEFINITIONS: IntegrationDefinition[] = [
  // AI
  {
    id: 'openai_integration',
    persistCategory: 'AI',
    uiCategory: 'ai',
    provider: 'OpenAI',
    titleHe: 'OpenAI',
    description: 'חיבור למודלי GPT (למשל GPT-4o) לשירות לקוחות ואוטומציות.',
    descriptionEn: 'Connect GPT models (e.g. GPT-4o) for support and automations.',
    mode: 'apiKey',
    modelOptions: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  },
  {
    id: 'google_gemini_integration',
    persistCategory: 'AI',
    uiCategory: 'ai',
    provider: 'Google Gemini',
    titleHe: 'גוגל Gemini',
    description: 'מודלי Gemini לטקסט ורב-מודאליות דרך Google AI.',
    descriptionEn: 'Gemini models for text and multimodal use via Google AI.',
    mode: 'apiKey',
    /** ריק — הרשימה נטענת מ-Google ListModels לפי המפתח (ראו מסך החיבור) */
    modelOptions: [],
  },
  {
    id: 'anthropic_integration',
    persistCategory: 'AI',
    uiCategory: 'ai',
    provider: 'Anthropic',
    titleHe: 'Anthropic Claude',
    description: 'מפעיל את עוזר ה-AI של Funner. המפתח נשמר מוצפן בשרת ומשותף לכל החברה.',
    descriptionEn: 'Powers the Funner AI assistant. The key is stored encrypted on the server, shared company-wide.',
    mode: 'apiKey',
    /** גיבוי סטטי — הרשימה המלאה נטענת מ-Anthropic בעת שמירת מפתח (עודכן 20.10) */
    modelOptions: ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5', 'claude-opus-5', 'claude-sonnet-4-6', 'claude-haiku-4-5'],
  },
  // Cloud Storage
  {
    id: 'google_drive_integration',
    persistCategory: 'Storage',
    uiCategory: 'cloud',
    provider: 'Google Drive',
    titleHe: 'גוגל דרייב',
    description: 'אחסון ושיתוף קבצים בענן — חיבור בדרך כלל ב-OAuth.',
    descriptionEn: 'Cloud file storage and sharing — usually connected via OAuth.',
    mode: 'oauth',
    oauthLabel: 'google',
  },
  {
    id: 'dropbox_integration',
    persistCategory: 'Storage',
    uiCategory: 'cloud',
    provider: 'Dropbox',
    titleHe: 'דרופבוקס',
    description: 'סנכרון קבצים ותיקיות עם Dropbox.',
    descriptionEn: 'Sync files and folders with Dropbox.',
    mode: 'oauth',
    oauthLabel: 'dropbox',
  },
  {
    id: 'onedrive_integration',
    persistCategory: 'Storage',
    uiCategory: 'cloud',
    provider: 'OneDrive',
    titleHe: 'וואן־דרייב',
    description: 'אחסון Microsoft 365 / OneDrive לעסקים.',
    descriptionEn: 'Microsoft 365 / OneDrive for business storage.',
    mode: 'oauth',
    oauthLabel: 'microsoft',
  },
  // Productivity
  {
    id: 'google_sheets_integration',
    persistCategory: 'Productivity',
    uiCategory: 'productivity',
    provider: 'Google Sheets',
    titleHe: 'גוגל שיטס',
    description: 'קריאה וכתיבה לגיליונות — חיבור Google.',
    descriptionEn: 'Read and write spreadsheets — Google connection.',
    mode: 'oauth',
    oauthLabel: 'google',
  },
  {
    id: 'excel_online_integration',
    persistCategory: 'Productivity',
    uiCategory: 'productivity',
    provider: 'Microsoft Excel',
    titleHe: 'אקסל אונליין',
    description: 'Excel Online / Graph — גיליונות בענן Microsoft.',
    descriptionEn: 'Excel Online / Microsoft Graph — cloud spreadsheets.',
    mode: 'oauth',
    oauthLabel: 'microsoft',
  },
  {
    id: 'slack_integration',
    persistCategory: 'Productivity',
    uiCategory: 'productivity',
    provider: 'Slack',
    titleHe: 'סלאק',
    description: 'הודעות וערוצים — אינטגרציית Slack.',
    descriptionEn: 'Messages and channels — Slack integration.',
    mode: 'oauth',
    oauthLabel: 'slack',
  },
];

export function definitionsForUiTab(tab: IntegrationUiCategory): IntegrationDefinition[] {
  return INTEGRATION_DEFINITIONS.filter((d) => d.uiCategory === tab);
}
